/**
 * P_ACTIVITY_OBSERVABILITY_7.0d — Activity admin page.
 *
 * Role-gated console at /admin/activity. Composes:
 *   • Tiny header — "Today: N · Week: N" (from /api/activity/health)
 *   • Optional sentence — "Recent patterns worth reviewing: ..."
 *     (from /api/activity/signals) — hidden when empty
 *   • Filters card — action / actor / resource / result / dates
 *   • Activity table — Actor | Action | Resource | Result | Time
 *
 * `source` exists in every row but is intentionally NOT a column —
 * implementation detail, not a UI primitive.
 *
 * Strictly no charts, no graphs, no KPI tiles, no exports, no badges,
 * no severity colors, no warning icons. Manual refresh only.
 */
import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Filter, RefreshCw, Loader2 } from 'lucide-react';
import api from '../lib/api';
// P_USER_WELCOME_DISPATCH_2.1 — proper UTC→local conversion.
import { fmtLocalDateTime } from '../utils/localTime';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../components/ui/select';

const RESULT_OPTIONS = ['', 'success', 'failure', 'unknown'];


export default function AdminActivity() {
    const { t, i18n } = useTranslation();

    // Filters
    const [action, setAction] = useState('');
    const [actorId, setActorId] = useState('');
    const [resourceType, setResourceType] = useState('');
    const [resourceId, setResourceId] = useState('');
    const [result, setResult] = useState('');
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');

    // Data
    const [items, setItems] = useState([]);
    const [count, setCount] = useState(0);
    const [health, setHealth] = useState({ today_count: 0, week_count: 0 });
    const [signals, setSignals] = useState([]);
    const [loading, setLoading] = useState(true);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const params = { limit: 100 };
            if (action) params.action = action;
            if (actorId.trim()) params.actor_id = actorId.trim();
            if (resourceType.trim()) params.resource_type = resourceType.trim();
            if (resourceId.trim()) params.resource_id = resourceId.trim();
            if (result) params.result = result;
            if (from) params.from = from;
            if (to) params.to = to;

            const [stream, hl, sg] = await Promise.all([
                api.get('/activity', { params }),
                api.get('/activity/health').catch(() => ({ data: { today_count: 0, week_count: 0 } })),
                api.get('/activity/signals').catch(() => ({ data: { signals: [] } })),
            ]);
            setItems(stream.data?.items || []);
            setCount(stream.data?.count || 0);
            setHealth(hl.data || { today_count: 0, week_count: 0 });
            setSignals(sg.data?.signals || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || e.message || 'Failed to load activity');
        } finally {
            setLoading(false);
        }
    }, [action, actorId, resourceType, resourceId, result, from, to]);

    useEffect(() => { reload(); }, [reload]);

    return (
        <div className="space-y-6 p-4 max-w-7xl mx-auto" data-testid="admin-activity-page">
            <header className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-semibold text-foreground">
                        {t('activity.title', 'Activity')}
                    </h1>
                    <p className="text-sm text-muted-foreground" data-testid="activity-header-counts">
                        {t('activity.today', 'Today')}: <span className="tabular-nums" data-testid="activity-today">{health.today_count}</span>
                        <span className="mx-2 text-muted-foreground/50">·</span>
                        {t('activity.week', 'Week')}: <span className="tabular-nums" data-testid="activity-week">{health.week_count}</span>
                    </p>
                </div>
                <Button variant="outline" size="sm" onClick={reload}
                    disabled={loading} data-testid="refresh-btn">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                    <span className="ml-2">{t('activity.refresh', 'Refresh')}</span>
                </Button>
            </header>

            {/* ── Patterns sentence — footnote, not a control panel ── */}
            {signals.length > 0 ? (
                <p className="text-sm text-muted-foreground" data-testid="activity-signals-sentence">
                    {t('activity.signalsPrefix', 'Recent patterns worth reviewing:')}{' '}
                    <span className="text-foreground font-mono">{signals.join(', ')}</span>
                </p>
            ) : null}

            {/* ── Filters ──────────────────────────────────────── */}
            <Card className="p-4" data-testid="filters-card">
                <div className="flex items-center gap-2 mb-2 text-sm text-muted-foreground">
                    <Filter className="w-4 h-4" />
                    {t('activity.filtersTitle', 'Filters')}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div>
                        <Label className="text-xs">{t('activity.action', 'Action')}</Label>
                        <Input value={action} onChange={(e) => setAction(e.target.value)}
                            placeholder={t('activity.actionPlaceholder', 'e.g. login_success')}
                            data-testid="filter-action" />
                    </div>
                    <div>
                        <Label className="text-xs">{t('activity.actorId', 'Actor ID')}</Label>
                        <Input value={actorId} onChange={(e) => setActorId(e.target.value)}
                            placeholder={t('activity.actorIdPlaceholder', 'Optional')}
                            data-testid="filter-actor-id" />
                    </div>
                    <div>
                        <Label className="text-xs">{t('activity.resourceType', 'Resource type')}</Label>
                        <Input value={resourceType} onChange={(e) => setResourceType(e.target.value)}
                            placeholder={t('activity.resourceTypePlaceholder', 'employee / user / ...')}
                            data-testid="filter-resource-type" />
                    </div>
                    <div>
                        <Label className="text-xs">{t('activity.resourceId', 'Resource ID')}</Label>
                        <Input value={resourceId} onChange={(e) => setResourceId(e.target.value)}
                            placeholder={t('activity.resourceIdPlaceholder', 'Optional')}
                            data-testid="filter-resource-id" />
                    </div>
                    <div>
                        <Label className="text-xs">{t('activity.result', 'Result')}</Label>
                        <Select value={result || 'any'} onValueChange={(v) => setResult(v === 'any' ? '' : v)}>
                            <SelectTrigger data-testid="filter-result"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="any" data-testid="filter-result-opt-any">{t('activity.any', 'Any')}</SelectItem>
                                {RESULT_OPTIONS.filter(Boolean).map((r) => (
                                    <SelectItem key={r} value={r} data-testid={`filter-result-opt-${r}`}>{r}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs">{t('activity.from', 'From')}</Label>
                        <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
                            data-testid="filter-from" />
                    </div>
                    <div>
                        <Label className="text-xs">{t('activity.to', 'To')}</Label>
                        <Input type="date" value={to} onChange={(e) => setTo(e.target.value)}
                            data-testid="filter-to" />
                    </div>
                </div>
            </Card>

            {/* ── Activity Table ───────────────────────────────── */}
            <Card className="p-0 overflow-hidden" data-testid="activity-card">
                <div className="px-4 py-2 text-xs text-muted-foreground border-b border-border">
                    {loading
                        ? t('activity.loading', 'Loading...')
                        : t('activity.matched', '{{count}} events', { count })}
                </div>
                {loading ? (
                    <div className="p-8 text-center" data-testid="activity-loading">
                        <Loader2 className="w-5 h-5 animate-spin mx-auto text-muted-foreground" />
                    </div>
                ) : items.length === 0 ? (
                    <div className="p-8 text-center text-sm text-muted-foreground"
                        data-testid="activity-empty">
                        {t('activity.empty', 'No activity in this window.')}
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="activity-table">
                            <thead className="bg-muted/40">
                                <tr className="text-left">
                                    <th className="px-4 py-2 font-medium">{t('activity.col.actor', 'Actor')}</th>
                                    <th className="px-4 py-2 font-medium">{t('activity.col.action', 'Action')}</th>
                                    <th className="px-4 py-2 font-medium">{t('activity.col.resource', 'Resource')}</th>
                                    <th className="px-4 py-2 font-medium">{t('activity.col.result', 'Result')}</th>
                                    <th className="px-4 py-2 font-medium">{t('activity.col.time', 'Time')}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {items.map((row) => (
                                    <tr key={`${row.source}-${row.id}`} data-testid={`activity-row-${row.id}`}>
                                        <td className="px-4 py-2 align-top">
                                            <div className="text-foreground" data-testid={`row-actor-${row.id}`}>
                                                {row.actor_email || row.actor_id || '—'}
                                            </div>
                                            {row.actor_role ? (
                                                <div className="text-xs text-muted-foreground">{row.actor_role}</div>
                                            ) : null}
                                        </td>
                                        <td className="px-4 py-2 align-top font-mono text-xs"
                                            data-testid={`row-action-${row.id}`}>
                                            {row.action || '—'}
                                        </td>
                                        <td className="px-4 py-2 align-top text-xs"
                                            data-testid={`row-resource-${row.id}`}>
                                            {row.resource_type
                                                ? <span><span className="text-muted-foreground">{row.resource_type}</span>{' '}<span className="font-mono">{(row.resource_id || '').slice(0, 12)}</span></span>
                                                : '—'}
                                        </td>
                                        <td className="px-4 py-2 align-top text-xs"
                                            data-testid={`row-result-${row.id}`}>
                                            {row.result}
                                        </td>
                                        <td className="px-4 py-2 align-top tabular-nums text-xs text-muted-foreground"
                                            data-testid={`row-time-${row.id}`}>
                                            {fmtLocalDateTime(row.timestamp, i18n.language) || '—'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>
        </div>
    );
}
